import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface CommercialModuleDefinition {
  id: string;
  moduleKey: string;
  name: string;
  description: string;
  status: string;
  sortOrder: number;
}

export interface CommercialPlanModuleAssignment {
  moduleKey: string;
  availability: string;
}

export interface CommercialPlan {
  id: string;
  code: string;
  name: string;
  tierRank: number | null;
  isPubliclySellable: boolean;
  pricingModel: string;
  catalogOfferingId: string | null;
  moduleAssignments: CommercialPlanModuleAssignment[];
}

export interface CommercialAddOn {
  id: string;
  code: string;
  name: string;
  description: string;
  moduleKey: string;
  billingMode: string;
  price: number;
  currency: string;
  linkedPlanId: string | null;
  status: string;
}

export interface CommercialProjectionSyncResult {
  projectionId: string;
  sourceType: string;
  sourceId: string;
  catalogOfferingId: string | null;
  projectionStatus: string;
  lastProjectedAtUtc: string | null;
  lastError: string;
}

export interface IndustrySolutionAssessmentLink {
  catalogItemId: string;
  sortOrder: number;
}

export interface IndustrySolution {
  id: string;
  code: string;
  slug: string;
  title: string;
  shortDescription: string;
  seoTitle: string;
  seoDescription: string;
  seoContentHtml: string;
  linkedModuleKeysJson: string;
  recommendedOfferingId: string | null;
  heroOfferingId: string | null;
  status: string;
  sortOrder: number;
  isDirectSaleAllowed: boolean;
  useOfferingAssessmentFallback: boolean;
  assessmentLinks: IndustrySolutionAssessmentLink[];
}

@Injectable({ providedIn: 'root' })
export class CommercialApiService {
  private readonly http = inject(HttpClient);
  private readonly commercialBase = `${environment.apiBaseUrl}/api/v1/commercial`;
  private readonly marketplaceBase = `${environment.apiBaseUrl}/api/v1/marketplace`;

  listModules(): Observable<CommercialModuleDefinition[]> {
    return this.http.get<CommercialModuleDefinition[]>(`${this.commercialBase}/modules`);
  }

  createModule(payload: {
    moduleKey: string;
    name: string;
    description: string;
    sortOrder: number;
  }): Observable<CommercialModuleDefinition> {
    return this.http.post<CommercialModuleDefinition>(`${this.commercialBase}/modules`, payload);
  }

  listPlans(): Observable<CommercialPlan[]> {
    return this.http.get<CommercialPlan[]>(`${this.commercialBase}/plans`);
  }

  updatePlan(
    planId: string,
    payload: {
      tierRank: number | null;
      isPubliclySellable: boolean;
      pricingModel: string;
      catalogOfferingId: string | null;
      moduleAssignments: CommercialPlanModuleAssignment[];
    }
  ): Observable<CommercialPlan> {
    return this.http.put<CommercialPlan>(`${this.commercialBase}/plans/${planId}`, payload);
  }

  listAddOns(): Observable<CommercialAddOn[]> {
    return this.http.get<CommercialAddOn[]>(`${this.commercialBase}/add-ons`);
  }

  createAddOn(payload: {
    code: string;
    name: string;
    description: string;
    moduleKey: string;
    billingMode: string;
    price: number;
    currency: string;
    linkedPlanId: string | null;
  }): Observable<CommercialAddOn> {
    return this.http.post<CommercialAddOn>(`${this.commercialBase}/add-ons`, payload);
  }

  syncProjection(payload: { sourceType: string; sourceId: string }): Observable<CommercialProjectionSyncResult> {
    return this.http.post<CommercialProjectionSyncResult>(`${this.commercialBase}/projections/sync`, payload);
  }

  listIndustrySolutions(): Observable<IndustrySolution[]> {
    return this.http.get<IndustrySolution[]>(`${this.marketplaceBase}/industry-solutions`);
  }

  createIndustrySolution(payload: {
    code: string;
    slug: string;
    title: string;
    shortDescription: string;
    seoTitle: string;
    seoDescription: string;
    seoContentHtml: string;
    linkedModuleKeysJson: string;
    recommendedOfferingId: string | null;
    heroOfferingId: string | null;
    status: string;
    sortOrder: number;
    useOfferingAssessmentFallback: boolean;
    assessmentLinks: IndustrySolutionAssessmentLink[];
  }): Observable<IndustrySolution> {
    return this.http.post<IndustrySolution>(`${this.marketplaceBase}/industry-solutions`, payload);
  }

  updateIndustrySolution(
    id: string,
    payload: {
      slug: string;
      title: string;
      shortDescription: string;
      seoTitle: string;
      seoDescription: string;
      seoContentHtml: string;
      linkedModuleKeysJson: string;
      recommendedOfferingId: string | null;
      heroOfferingId: string | null;
      status: string;
      sortOrder: number;
      useOfferingAssessmentFallback: boolean;
      assessmentLinks: IndustrySolutionAssessmentLink[];
    }
  ): Observable<IndustrySolution> {
    return this.http.put<IndustrySolution>(`${this.marketplaceBase}/industry-solutions/${id}`, payload);
  }
}
